import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import mixedSentimentIcon from "../../../assets/icons/mixedSentiment.png";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";

import SampleSizeNew from "../../common/SampleSizeNew";
import TextThemeBarChart from "../chart/TextThemeBarChart";
import TextThemeWebChart from "../chart/TextThemeWebChart";

function RefreshDotIcon() {
    return (
        <svg
            aria-hidden="true"
            className="tta-view-more-icon"
            fill="none"
            viewBox="0 0 24 24"
        >
            <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2m-.5 -4v4h4" />
            <path d="M4 13a8.1 8.1 0 0 0 15.5 2m.5 4v-4h-4" />
            <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />
        </svg>
    );
}

function filterEntries(filters) {
    return Object.entries(filters || {}).filter(([, values]) => values?.length);
}

function getPreviewText(value, maxLength = 85) {
    const text = value || "";
    return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;
}

function ChartToggle({ value, disabled, onChange }) {
    return (
        <div className={disabled ? "tta-chart-toggle-switch disabled" : "tta-chart-toggle-switch"}>
            <button
                type="button"
                className={value === "bar" ? "active" : ""}
                onClick={() => onChange("bar")}
                disabled={disabled}
            >
                Bar Chart
            </button>
            <Switch
                size="small"
                checked={value === "web"}
                disabled={disabled}
                onChange={(event) => onChange(event.target.checked ? "web" : "bar")}
            />
            <button
                type="button"
                className={value === "web" ? "active" : ""}
                onClick={() => onChange("web")}
                disabled={disabled}
            >
                Web Chart
            </button>
        </div>
    );
}

function DisplayLabelDropdown({ labels, selectedId, disabled, onChange }) {
    const [open, setOpen] = useState(false);
    const selectedLabel = labels.find((label) => label.id === selectedId) || labels[0];
    const hasOptions = labels.length > 1;

    return (
        <div className="tta-display-dropdown">
            <button
                type="button"
                disabled={disabled}
                onClick={() => hasOptions && setOpen(!open)}
            >
                <span className="tta-display-prefix">({selectedLabel.prefix})</span>
                <span>{selectedLabel.label}</span>
                {hasOptions && (
                    <KeyboardArrowDownIcon className={open ? "color-blue tta-dropdown-chevron open" : "color-blue tta-dropdown-chevron"} />
                )}
            </button>
            {open && hasOptions && !disabled && (
                <div className="tta-display-menu">
                    {labels.filter((label) => label.id !== selectedId).map((label) => (
                        <button
                            type="button"
                            key={label.id}
                            onClick={() => {
                                onChange(label.id);
                                setOpen(false);
                            }}
                        >
                            <span className="tta-display-prefix">({label.prefix})</span>
                            <span>{label.label}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function LocalFilterButton({ product, appliedFilters, filterStatus, disabled, onApply }) {
    const categories = Object.keys(product.availableFilters || {});
    const [open, setOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState(categories[0] || "");
    const [draftFilters, setDraftFilters] = useState(appliedFilters || {});

    useEffect(() => {
        setDraftFilters(appliedFilters || {});
    }, [appliedFilters]);

    const statusApplied = filterStatus?.applied || {};
    const statusMissing = filterStatus?.missing || {};
    const activeFilters = filterStatus ? statusApplied : appliedFilters;
    const hasAppliedFilters = filterEntries(activeFilters).length > 0;
    const hasMissingFilters = filterEntries(statusMissing).length > 0;

    const updateDraftFilters = (category, values) => {
        const nextFilters = { ...draftFilters, [category]: values };
        const pairedAgeFilter = {
            "Age Range": "Consumer Exact Age",
            "Consumer Exact Age": "Age Range",
        }[category];

        if (values.length && pairedAgeFilter) {
            nextFilters[pairedAgeFilter] = [];
        }

        setDraftFilters(nextFilters);
    };

    const toggleValue = (value) => {
        const values = new Set(draftFilters[activeCategory] || []);
        if (values.has(value)) values.delete(value);
        else values.add(value);
        updateDraftFilters(activeCategory, Array.from(values));
    };

    const tooltip = (
        <div className="tta-filter-tooltip">
            {hasAppliedFilters && <div>
                <strong>Applied Filters</strong>
                {filterEntries(activeFilters).map(([category, values]) => (
                    <p key={category}>
                        <b>{category}:</b> {values.join(", ")}
                    </p>
                ))}
            </div>}
            {hasMissingFilters && (
                <div>
                    <strong>Missing Filters</strong>
                    {filterEntries(statusMissing).map(([category, values]) => (
                        <p key={category}>
                            <b>{category}:</b> {values.join(", ")}
                        </p>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="tta-filter-wrapper">
            <Tooltip
                title={hasAppliedFilters || hasMissingFilters ? tooltip : ""}
                placement="top"
                componentsProps={{
                    tooltip: {
                        className: "tta-ui-tooltip",
                    },
                }}
            >
                <span>
                    <Button
                        size="small"
                        variant="outlined"
                        disabled={disabled || !categories.length}
                        className="custom-btn custom-btn-outlined-2 color-blue bold-content tta-filter-button"
                        startIcon={<ManageAccountsIcon />}
                        onClick={() => setOpen(!open)}
                    >
                        Filters
                        {hasAppliedFilters && <span className="tta-status-dot blue" />}
                        {hasMissingFilters && <span className="tta-status-dot red" />}
                    </Button>
                </span>
            </Tooltip>
            {open && !disabled && (
                <div className="tta-filter-menu box-shadow--2">
                    <div className="tta-filter-categories liner-light-blue-background">
                        {categories.map((category) => (
                            <button
                                type="button"
                                key={category}
                                className={activeCategory === category ? "active" : ""}
                                onClick={() => setActiveCategory(category)}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                    <div className="tta-filter-values">
                        <div className="tta-filter-actions">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={(draftFilters[activeCategory] || []).length === (product.availableFilters[activeCategory] || []).length}
                                    onChange={(event) => {
                                        updateDraftFilters(
                                            activeCategory,
                                            event.target.checked
                                                ? product.availableFilters[activeCategory]
                                                : []
                                        );
                                    }}
                                />
                                Select All
                            </label>
                            <button
                                type="button"
                                className="clean-button color-blue"
                                onClick={() => updateDraftFilters(activeCategory, [])}
                            >
                                Clear All
                            </button>
                        </div>
                        {(product.availableFilters[activeCategory] || []).map((value) => (
                            <label
                                className="tta-filter-checkbox"
                                key={value}
                            >
                                <input
                                    type="checkbox"
                                    checked={(draftFilters[activeCategory] || []).includes(value)}
                                    onChange={() => toggleValue(value)}
                                />
                                {value}
                            </label>
                        ))}
                        <button
                            type="button"
                            className="tta-apply-button custom-btn custom-btn-outlined-2 background-color-white color-blue bold-content"
                            onClick={() => {
                                onApply(draftFilters);
                                setOpen(false);
                            }}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function VerbatimPanel({ responses, disabled, onViewMore }) {
    return (
        <aside className="tta-verbatim-panel">
            {responses.slice(0, 2).map((response) => (
                <div
                    className="tta-verbatim"
                    key={`${response.quote}-${response.ageRange}`}
                >
                    {response.sentiment === "Exclusively Positive" ? (
                        <ThumbUpAltOutlinedIcon className="tta-verbatim-icon positive" />
                    ) : response.sentiment === "Exclusively Negative" ? (
                        <ThumbDownAltOutlinedIcon className="tta-verbatim-icon negative" />
                    ) : (
                        <img
                            src={mixedSentimentIcon}
                            className="tta-verbatim-icon mixed"
                            alt="Both positive and negative"
                        />
                    )}
                    <Tooltip
                        title={response.quote}
                        placement="left"
                        componentsProps={{
                            tooltip: {
                                className: "tta-verbatim-tooltip",
                            },
                        }}
                    >
                        <p>{`"${getPreviewText(response.quote)}"`}</p>
                    </Tooltip>
                    <strong>
                        {response.ageRange} {response.gender}, {response.country}
                    </strong>
                </div>
            ))}
            <button
                type="button"
                disabled={disabled}
                className="tta-view-more"
                onClick={onViewMore}
            >
                <RefreshDotIcon />
                View More
            </button>
        </aside>
    );
}

function SummarySection({ summaryText }) {
    const [expanded, setExpanded] = useState(false);
    const displayText = summaryText || "Summary generation is not available for the selected response set.";
    const canExpand = displayText.length > 180;

    return (
        <div className="tta-summary">
            <Tooltip
                title="Summary Generated by ChatGPT 5"
                placement="top"
                componentsProps={{
                    tooltip: {
                        className: "tta-ui-tooltip",
                    },
                }}
            >
                <InfoOutlinedIcon className="color-blue" />
            </Tooltip>
            <div className="tta-summary-content">
                <span className={expanded ? "tta-summary-text" : "tta-summary-text clamped"}>{displayText}</span>
                {canExpand && (
                    <button
                        type="button"
                        className="clean-button color-blue tta-summary-toggle"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? "Show less" : "View more"}
                    </button>
                )}
            </div>
        </div>
    );
}

function ProductCardLayout({ product, tabState, onCardFilterChange, onDisplayLabelChange, onChartModeChange, filterStatus, onViewMore }) {
    const selectedDisplayId = tabState.selectedDisplayLabels[product.id] || product.displayLabels[0]?.id;
    const selectedDisplay = product.displayLabels.find((item) => item.id === selectedDisplayId) || product.displayLabels[0];
    const chartMode = tabState.chartModes[product.id] || "bar";
    const appliedFilters = tabState.cardFilters[product.id] || {};
    const themes = selectedDisplay?.themes || product.themes;
    const verbatimResponses = selectedDisplay?.verbatimResponses || product.verbatimResponses;
    const summary = selectedDisplay?.summary ?? product.summary;
    const noData =
        product.isNoData ||
        (product.sampleSize > 10 &&
            themes.length === 1 &&
            themes[0].frequency < 4);
    const displaySampleSize = product.sampleSize;

    return (
        <article className="tta-card background-color-white box-shadow--2">
            <div className="tta-card-title-row">
                <span className="tta-sample-size-wrapper">
                    <SampleSizeNew
                        productData={{ sampleSize: displaySampleSize }}
                        alwaysShowIcon
                    />
                </span>
                <button
                    type="button"
                    className="table-head-product-buttons text-left"
                >
                    {product.productName} / {product.studyReference}
                </button>
            </div>
            <div className="tta-controls">
                <DisplayLabelDropdown
                    labels={product.displayLabels}
                    selectedId={selectedDisplayId}
                    onChange={(nextId) => onDisplayLabelChange(product.id, nextId)}
                />
                <LocalFilterButton
                    product={product}
                    appliedFilters={appliedFilters}
                    filterStatus={filterStatus}
                    onApply={(nextFilters) => onCardFilterChange(product.id, nextFilters)}
                />
                <ChartToggle
                    value={chartMode}
                    disabled={noData || displaySampleSize <= 10}
                    onChange={(nextMode) => onChartModeChange(product.id, nextMode)}
                />
            </div>
            {displaySampleSize > 10 ? (
                <>
                    <div className={noData ? "tta-analysis-frame no-verbatim" : "tta-analysis-frame"}>
                        <div className={themes.length > 3 ? "tta-chart-area scrollable" : "tta-chart-area"}>
                            {chartMode === "web" ? (
                                <TextThemeWebChart
                                    data={selectedDisplay.webChartData}
                                    noData={noData}
                                />
                            ) : (
                                <TextThemeBarChart
                                    data={themes}
                                    noData={noData}
                                />
                            )}
                        </div>
                        {!noData && (
                            <VerbatimPanel
                                responses={verbatimResponses}
                                disabled={noData}
                                onViewMore={() => onViewMore(product, selectedDisplay)}
                            />
                        )}
                    </div>
                    {noData && (
                        <div className="tta-warning color-disabled">
                            <WarningAmberOutlinedIcon fontSize="small" />
                            <span>The amount of consumer data and themes identified by GenAI is currently insufficient to support meaningful or insightful interpretation</span>
                        </div>
                    )}
                    <div className="tta-question color-black">{selectedDisplay.openEndedQuestion}</div>
                    <SummarySection summaryText={summary} />
                </>
            ) : (
                <div 
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "300px",
                    }}
                >
                    <div 
                        style={{
                            width : "80%",
                            margin: "0 auto",
                            fontSize: "14px",
                            

                        }}
                    >Theme Text Analysis cannot be performed when the sample is equal or below 10 respondents, in line with Unilever’s AI ethics and data protection principles, to reduce re-identification risk</div>
                </div>
            )}
        </article>
    );
}

ChartToggle.propTypes = {
    value: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
};

DisplayLabelDropdown.propTypes = {
    labels: PropTypes.array,
    selectedId: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
};

LocalFilterButton.propTypes = {
    product: PropTypes.object,
    appliedFilters: PropTypes.object,
    filterStatus: PropTypes.object,
    disabled: PropTypes.bool,
    onApply: PropTypes.func,
};

VerbatimPanel.propTypes = {
    responses: PropTypes.array,
    disabled: PropTypes.bool,
    onViewMore: PropTypes.func,
};

SummarySection.propTypes = {
    summaryText: PropTypes.string,
};

ProductCardLayout.propTypes = {
    product: PropTypes.object,
    tabState: PropTypes.object,
    onCardFilterChange: PropTypes.func,
    onDisplayLabelChange: PropTypes.func,
    onChartModeChange: PropTypes.func,
    filterStatus: PropTypes.object,
    onViewMore: PropTypes.func,
};

export default ProductCardLayout;
